/**
 * Armored — the RUN-AND-GUN heavy trooper (step 3, +ranged in the step-4 polish).
 *
 * A slow, heavy ground trooper (Walker ground spine) that is UN-stompable — any contact hurts,
 * like a spiked WALKER. A BULLET or the combat SWORD chips it: the shared hp-soak seam
 * (Sim.chipOrKill, driven by both killEnemiesInBox and resolveAttackHitbox) drops `hp` one hit at
 * a time until 0. ARMORED is the reason the `Entity.hp` spine field exists.
 *
 * Behaviour: PATROLS while the player is out of range; once the player is in horizontal range at
 * its own height, it HALTS, turns to face them, braces (telegraph), and fires a heavy enemy
 * PROJECTILE — a slower, tankier cousin of the GUNNER (playtest: an armored that only slid around
 * "did nothing"; now it's a ranged threat). Renders a 2-frame walk while patrolling + a braced
 * fire pose while shooting.
 *
 * Determinism: gravity + abs/min/max + integer counters; the shot emits through
 * ctx.spawnEntityAt's deterministic queue. No random / wall-clock / transcendentals.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';
import { PROJECTILE_H } from './Projectile';

const GRAVITY = 2050;
const TERMINAL = 1200;
const DEFAULT_SPEED = 38;
const DEFAULT_HP = 3;
const SQUASH_SECONDS = 0.4;
const DEFAULT_COOLDOWN = 140;   // slower than the GUNNER (110) — a heavy, deliberate shooter
const DEFAULT_TELEGRAPH = 20;
const DEFAULT_BULLET_SPEED = 280; // heavier/slower round than the gunner's 340
const DEFAULT_BULLET_LIFE = 180;
const DEFAULT_RANGE_PX = 700;
const V_RANGE_PX = 150;

export class Armored implements Entity {
  readonly type = EntityType.ARMORED;
  body: AABB;
  vx: number;
  vy = 0;
  facing: 1 | -1 = -1;
  readonly spriteFacesLeft = true; // Kenney enemy art faces left
  alive = true;
  hp: number;
  squashTimer = 0;

  private speed: number;
  private turnAtLedge: boolean;
  private cooldown: number;
  private telegraph: number;
  private bulletSpeed: number;
  private bulletLife: number;
  private rangePx: number;
  private sinceFire = 0;
  private firePose = false;
  private walkPhase = 0;

  constructor(cx: number, feetY: number, props?: Record<string, unknown>, w = 54, h = 52) {
    this.body = { x: cx - w / 2, y: feetY - h, w, h };
    this.speed = typeof props?.speed === 'number' ? props.speed : DEFAULT_SPEED;
    this.turnAtLedge = props?.turnAtLedge !== false;
    this.hp = typeof props?.hp === 'number' ? Math.max(1, Math.floor(props.hp)) : DEFAULT_HP;
    this.cooldown = typeof props?.fireCooldownSteps === 'number'
      ? Math.max(20, Math.floor(props.fireCooldownSteps)) : DEFAULT_COOLDOWN;
    this.telegraph = typeof props?.telegraphSteps === 'number'
      ? Math.max(1, Math.floor(props.telegraphSteps)) : DEFAULT_TELEGRAPH;
    this.bulletSpeed = typeof props?.bulletSpeed === 'number' ? props.bulletSpeed : DEFAULT_BULLET_SPEED;
    this.bulletLife = typeof props?.bulletLifeSteps === 'number'
      ? Math.floor(props.bulletLifeSteps) : DEFAULT_BULLET_LIFE;
    this.rangePx = typeof props?.rangePx === 'number' ? props.rangePx : DEFAULT_RANGE_PX;

    const dir = props?.dir;
    if (dir === 1 || dir === '1' || dir === 'right') this.facing = 1;
    else if (dir === -1 || dir === '-1' || dir === 'left') this.facing = -1;
    this.vx = this.facing * this.speed;
  }

  update(dt: number, ctx: EntityContext): void {
    if (!this.alive) { this.squashTimer = Math.max(0, this.squashTimer - dt); return; }
    const grid = ctx.grid;

    // gravity + ground (always)
    this.vy = Math.min(this.vy + GRAVITY * dt, TERMINAL);
    if (grid.collideY(this.body, this.vy * dt, false).grounded) this.vy = 0;

    const pcx = ctx.player.body.x + ctx.player.body.w / 2;
    const scx = this.body.x + this.body.w / 2;
    const dx = Math.abs(pcx - scx);
    const dyFeet = Math.abs((ctx.player.body.y + ctx.player.body.h) - (this.body.y + this.body.h));
    const inRange = dx <= this.rangePx && dyFeet <= V_RANGE_PX;

    if (inRange) {
      // halt, face the player, brace + fire
      this.facing = pcx < scx ? -1 : 1;
      this.vx = 0;
      this.sinceFire++;
      this.firePose = this.sinceFire >= this.cooldown - this.telegraph;
      if (this.sinceFire >= this.cooldown) { this.fire(ctx); this.sinceFire = 0; this.firePose = false; }
    } else {
      // patrol (reverse at wall / ledge)
      this.firePose = false;
      this.sinceFire = 0;
      this.vx = this.facing * this.speed;
      if (grid.collideX(this.body, this.vx * dt)) this.reverse();
      if (this.turnAtLedge) {
        const aheadX = this.facing > 0 ? this.body.x + this.body.w + 2 : this.body.x - 2;
        const belowY = this.body.y + this.body.h + 4;
        if (!grid.solidBelowPoint(aheadX, belowY)) this.reverse();
      }
    }

    this.walkPhase += Math.abs(this.vx) * dt; // 0 while braced/firing → shows the fire/rest pose
  }

  private fire(ctx: EntityContext): void {
    const muzzleCx = this.body.x + this.body.w / 2 + this.facing * (this.body.w * 0.5 + 4);
    const muzzleCy = this.body.y + this.body.h * 0.45;
    ctx.spawnEntityAt(EntityType.PROJECTILE, muzzleCx, muzzleCy + PROJECTILE_H / 2, {
      vx: this.facing * this.bulletSpeed, vy: 0, life: this.bulletLife, owner: 'enemy',
    });
    ctx.playSound('shoot', 0.35, 0.42); // POLISH_01 #1 — deep heavy-cannon shot
  }

  /** UN-stompable: any contact costs a heart (like a spiked WALKER). A bullet/sword chips it; a stomp never kills it. */
  onPlayerOverlap(ctx: EntityContext): void { ctx.hurtPlayer(); }

  /**
   * Damageable (hp-soak): damageable()=true lets isAttackKillable route it into BOTH attack seams
   * — the bullet (killEnemiesInBox) and the combat sword (resolveAttackHitbox) — where the shared
   * chipOrKill CHIPS hp one hit per shot/swing (kills at 0). onPlayerOverlap never routes a stomp
   * to killEntity, so a stomp still only hurts the player. F1 (2026-07-01): before the sword shared
   * chipOrKill it one-shot this; now the sword soaks hp like the bullet.
   */
  damageable(): boolean { return true; }

  /** Non-lethal chip feedback (hp still >0 after a hit). Real flash/clink = a feel pass. */
  onHit(ctx: EntityContext): void { ctx.playSound('bump', 0.3, 0.9); } // POLISH_01 #1 — armor clink

  onKilled(): void {
    if (!this.alive && this.squashTimer > 0) return;
    this.alive = false;
    this.squashTimer = SQUASH_SECONDS;
    this.vx = 0;
  }

  private get walkFrameA(): boolean { return Math.floor(this.walkPhase / 40) % 2 === 0; }

  spriteKey(): string {
    if (!this.alive) return 'armored_rest';
    if (this.firePose) return 'armored_fire';
    if (Math.abs(this.vx) > 1) return this.walkFrameA ? 'armored_walk_1' : 'armored_walk_2';
    return 'armored_rest';
  }
  visible(): boolean { return this.alive || this.squashTimer > 0; }

  private reverse(): void {
    this.facing = this.facing > 0 ? -1 : 1;
    this.vx = this.facing * this.speed;
  }
}
